import { useState } from 'react';
import type {
  AutoChartFamily,
  AutoCreativeCard,
  PortalAutoPanel,
  PortalMetaAutomotiveReport,
} from '../../../../../types/portalAutomotive';
import type { PortalMetaReport, PortalReportLabels } from '../../../../../types/portalReport';
import {
  AUTO_COST_PER_LABELS,
  AUTO_COUNT_LABELS,
  autoCostPerOrNull,
  autoPanelIsEmpty,
  autoPanelSpecFor,
  autoPanelVisible,
  contactsVisible,
  visibleExtras,
  type AutoPanelSpec,
} from '../../../../../lib/portalReport/automotiveReport';
import type { ChartMetric } from '../../../../../lib/portalReport/chartPlan';
import { isGroupScope } from '../../../../../lib/portalReport/groupScope';
import { Band, Figure, SectionHeading, SwitchGroup } from './controls';
import { ReportChartCard } from './ReportChartCard';
import { ReportHero, type HeroStat } from './ReportHero';
import { AudiencePanel, DeliveryDiagnostics } from './ReportPanels';
import { DASH, formatReportDate, moneyFull, moneyRound, numberFull } from './format';

// CLIENT PORTAL RESTYLE — the AUTOMOTIVE Meta page (2026-11-11, reshaped 2026-11-12).
//
// ⚠️⚠️ WHAT EACH PANEL SHOWS IS `AUTO_PANELS` in
// src/lib/portalReport/automotiveReport.ts, and WHETHER one is drawn is
// `autoPanelVisible` there; this file only draws them. A cost-per that is not in a
// panel's spec is not drawn, because it is not in the payload.
//
// ⚠️ EACH CAMPAIGN TYPE IS A FULL-WIDTH SECTION, STACKED, NEVER TWO COLUMNS (Round 5
// §B), opening on a large section heading, then its figures, charts, campaign table
// and creatives. The charts inside a section may sit side by side; the SECTIONS never do.
//
// ⚠️ CONTACT is hidden everywhere it is zero in both periods (`contactsVisible`),
// one answer for the headline tile, the home card and every per-panel extra.
//
// ⚠️ ROAS appears nowhere, and there are no platform logos, as on every other page.

/** The headline — leads at the top, with the page's own figures beneath it. */
export function AutomotiveMetaHeadline({
  auto,
  range,
}: {
  auto: PortalMetaAutomotiveReport;
  range: { from: string; to: string };
}) {
  const h = auto.headline;
  const stat = (label: string, value: number): HeroStat => ({
    label,
    value: numberFull(value),
    delta: null,
    metric: 'leads',
    unavailable: null,
  });
  const stats: HeroStat[] = [
    stat('Meta leads', h.metaLeads),
    stat('Website leads', h.websiteLeads),
    stat('Content views', h.contentViews),
    // ⚠️ Contact is a STAT only when it exists in this period or its comparison.
    ...(contactsVisible(h) ? [stat('Contacts', h.contacts)] : []),
  ];
  return (
    <ReportHero
      eyebrow={`Meta · ${formatReportDate(range.from)} – ${formatReportDate(range.to)}`}
      headline={numberFull(h.leads)}
      intro="Meta leads and website leads for this period, each counted once. Content views are counted separately."
      stats={stats}
    />
  );
}

function countText(panel: PortalAutoPanel, key: keyof PortalAutoPanel['counts']): string {
  return numberFull(panel.counts[key]);
}

/**
 * One chart family as a chart card. ⚠️ The count rides in `leads` and the
 * cost-per in `costPerLead` (the goal-split convention the card's `goal` source
 * reads), so the chips AND the accessible name are told what is really plotted.
 *
 * ⚠️⚠️ EVERY CARD IS TITLED "<section> · <selected metric>" and carries metric chips
 * (Round 5 §A). The section is REQUIRED and a blank one throws inside the card, so a
 * card cannot render without a title. The chips are the count, its cost-per when the
 * family has one, and Spend whenever the rows were one currency.
 */
export function AutoFamilyChart({
  family,
  section,
  labels,
  fallbackCurrency,
  today,
}: {
  family: AutoChartFamily;
  /** The section the card sits in — the first half of its title. */
  section: string;
  labels: PortalReportLabels;
  fallbackCurrency: string;
  today: string;
}) {
  const metricLabel = AUTO_COUNT_LABELS[family.metric];
  const metrics: ChartMetric[] = family.costPer
    ? ['leads', 'costPerLead', 'spend']
    : family.currency !== null
      ? ['leads', 'spend']
      : ['leads'];
  return (
    <ReportChartCard
      title={section}
      titleSection={section}
      today={today}
      source={{ kind: 'goal', period: family.period, months: family.months }}
      metrics={metrics}
      labels={{
        ...labels,
        leads: metricLabel,
        leadsShort: metricLabel,
        ...(family.costPer ? { costPerLead: AUTO_COST_PER_LABELS[family.costPer] } : {}),
      }}
      currency={family.currency ?? fallbackCurrency}
      periodLabel="this period"
    />
  );
}

/** How many creative cards show before "See all". */
export const CREATIVES_SHOWN = 4;

/** The figures one creative card shows, per panel — Round 2 §2. */
export function creativeFigures(
  bucket: PortalAutoPanel['bucket'],
  c: AutoCreativeCard,
): Array<{ label: string; value: string }> {
  const costPerView = autoCostPerOrNull(c.spend, c.contentViews);
  const leads = c.metaLeads + c.websiteLeads;
  const money = (v: number | null) => (v === null ? DASH : moneyFull(v, c.currency));
  switch (bucket) {
    case 'aia':
      return [
        { label: 'Content views', value: numberFull(c.contentViews) },
        { label: 'Cost per content view', value: money(costPerView) },
        { label: 'Leads', value: numberFull(leads) },
      ];
    case 'meta_lead':
      return [
        { label: 'Leads', value: numberFull(c.metaLeads) },
        { label: 'Cost per lead', value: money(autoCostPerOrNull(c.spend, c.metaLeads)) },
      ];
    case 'website_lead':
      return [
        { label: 'Website leads', value: numberFull(c.websiteLeads) },
        { label: 'Cost per website lead', value: money(autoCostPerOrNull(c.spend, c.websiteLeads)) },
      ];
    case 'content_view':
      return [
        { label: 'Content views', value: numberFull(c.contentViews) },
        { label: 'Cost per content view', value: money(costPerView) },
      ];
    case 'other':
      return [
        { label: 'Landing page views', value: numberFull(c.landingPageViews) },
        { label: 'Cost per landing page view', value: money(autoCostPerOrNull(c.spend, c.landingPageViews)) },
      ];
    case 'uncategorised':
      return [{ label: 'Leads', value: numberFull(leads) }];
  }
}

/**
 * The panel's own top creatives, with "See all N creatives".
 *
 * ⚠️ NO BROKEN IMAGE, EVER. A catalogue (AIA / DPA) ad has no single static image,
 * so a missing thumbnail on the AIA panel reads "Catalogue ad" rather than a
 * generic "No preview" — it is a normal state, not a fault. Reuses the report's own
 * creative card styling (`rpt-creatives`), not a second pipeline.
 *
 * ⚠️ CREATIVES ARE MERGED ACROSS ADS server-side (`mergeAutoCreatives`, the report's
 * existing merge rule), so one creative trafficked as several ads is ONE card whose
 * figures are the combined ones; a card that merged says so.
 *
 * ⚠️ `title` has already been through the PII guard server-side (safeCreativeTitle);
 * this component never sees a raw ad name.
 */
export function AutoPanelCreatives({ panel }: { panel: PortalAutoPanel }) {
  const [all, setAll] = useState(false);
  if (panel.creatives.length === 0) return null;
  const shown = all ? panel.creatives : panel.creatives.slice(0, CREATIVES_SHOWN);
  return (
    <div data-auto-creatives={panel.bucket} style={{ marginTop: 14 }}>
      <p className="rpt-mono rpt-stat-label">Top creative</p>
      <div className="rpt-creatives">
        {shown.map((c) => (
          <div key={`${c.adId}-${c.currency}`}>
            <div className="rpt-thumb">
              {c.thumbnailUrl ? (
                <img src={c.thumbnailUrl} alt="" loading="lazy" />
              ) : (
                <span className="rpt-mono">{panel.bucket === 'aia' ? 'Catalogue ad' : 'No preview'}</span>
              )}
            </div>
            {c.status && <span className="rpt-pill">{c.status}</span>}
            <p className="rpt-creative-name">{c.title}</p>
            {c.mergedAdCount > 1 && (
              <p className="rpt-sub rpt-muted" data-merged-ads>
                Run as {c.mergedAdCount} ads, combined
              </p>
            )}
            <div className="rpt-crow">
              <span>Spend</span>
              <b>{moneyRound(c.spend, c.currency)}</b>
            </div>
            {creativeFigures(panel.bucket, c).map((f) => (
              <div key={f.label} className="rpt-crow">
                <span>{f.label}</span>
                <b>{f.value}</b>
              </div>
            ))}
          </div>
        ))}
      </div>
      {panel.creativesTotal > CREATIVES_SHOWN && (
        <div style={{ marginTop: 10 }}>
          <SwitchGroup
            ariaLabel="Creatives shown"
            value={all ? 'all' : 'top'}
            onChange={(next) => setAll(next === 'all')}
            options={[
              { value: 'top' as const, label: `Top ${CREATIVES_SHOWN}` },
              { value: 'all' as const, label: `See all ${panel.creativesTotal} creatives` },
            ]}
          />
        </div>
      )}
    </div>
  );
}

function AutoCampaignTable({ panel }: { panel: PortalAutoPanel }) {
  if (panel.campaigns.length === 0) return null;
  return (
    <div className="rpt-table-wrap" style={{ marginTop: 14 }}>
      <table className="rpt-table">
        <caption className="rpt-visually-hidden">Vehicle inventory campaigns and the goal each was set up for</caption>
        <thead>
          <tr>
            <th scope="col">Campaign</th>
            <th scope="col">Goal</th>
            <th scope="col">Leads</th>
            <th scope="col">Cost per lead</th>
            <th scope="col">Content views</th>
            <th scope="col">Cost per view</th>
            <th scope="col">Spend</th>
          </tr>
        </thead>
        <tbody>
          {panel.campaigns.map((c) => {
            const leads = c.metaLeads + c.websiteLeads;
            const cost = (v: number | null) => (v === null ? DASH : moneyFull(v, c.currency));
            return (
              <tr key={`${c.campaignId}-${c.currency}`}>
                <th scope="row">{c.name}</th>
                {/* ⚠️ The goal it WOULD have had without the AIA rule — so a
                    lead-goal AIA campaign and a content-view-goal one read apart. */}
                <td>{GOAL_COLUMN_LABEL[c.goalBucket]}</td>
                <td>{numberFull(leads)}</td>
                <td>{cost(autoCostPerOrNull(c.spend, leads))}</td>
                <td>{numberFull(c.contentViews)}</td>
                <td>{cost(autoCostPerOrNull(c.spend, c.contentViews))}</td>
                <td>{moneyRound(c.spend, c.currency)}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

const GOAL_COLUMN_LABEL: Record<PortalAutoPanel['bucket'], string> = {
  aia: 'Vehicle inventory',
  meta_lead: 'Meta lead',
  website_lead: 'Website lead',
  content_view: 'Content view',
  other: 'Other',
  uncategorised: 'Unknown',
};

/** The sentence under "Results by campaign type" — Round 5 §B9, verbatim. */
export const AUTO_RESULTS_INTRO =
  'Each campaign is counted in one section. Vehicle inventory (AIA) campaigns are grouped by name and show both cost per lead and cost per content view; other sections show the cost for the goal they were set up for.';

/**
 * ONE campaign type as a FULL-WIDTH section: a large heading, its figures, its
 * charts, its campaign table and its creatives. ⚠️ A section is its own band, so two
 * of them can never share a row.
 */
function AutoPanelSection({
  panel,
  spec,
  showContacts,
  labels,
  today,
  alternate,
}: {
  panel: PortalAutoPanel;
  spec: AutoPanelSpec;
  showContacts: boolean;
  labels: PortalReportLabels;
  today: string;
  alternate: boolean;
}) {
  const empty = autoPanelIsEmpty(panel);
  const money = panel.money.length > 0 ? panel.money : null;
  const extras = visibleExtras(spec, showContacts);
  const fallbackCurrency = panel.money[0]?.currency ?? 'AUD';
  return (
    <Band variant={alternate ? 'contrast' : undefined}>
      <section aria-label={spec.title} data-auto-bucket={panel.bucket} data-auto-section>
        <SectionHeading title={spec.title} />
        {spec.note && !empty && <p className="rpt-note rpt-muted">{spec.note}</p>}
        {empty && spec.emptySentence ? (
          // ⚠️ The always-on sections show their structure every month; when nothing
          // ran it is ONE sentence, not a column of zeros that reads as a bad result.
          <p className="rpt-note rpt-muted" data-auto-empty>
            {spec.emptySentence}
          </p>
        ) : (
          <>
            <div className="rpt-card">
              <div className="rpt-stat-grid">
                {spec.primary.map((k) => (
                  <p key={k} className="rpt-stat">
                    <Figure value={countText(panel, k)} className="rpt-card-figure" />
                    <span className="rpt-mono rpt-stat-label">{AUTO_COUNT_LABELS[k]}</span>
                  </p>
                ))}
                {spec.costPer.map((cp) => (
                  <p key={cp.key} className="rpt-stat">
                    <Figure
                      value={
                        money
                          ? money
                              .map((m) => {
                                const v = m.costPer[cp.key];
                                return v === null || v === undefined ? DASH : moneyFull(v, m.currency);
                              })
                              .join(' · ')
                          : null
                      }
                      className="rpt-card-figure"
                    />
                    <span className="rpt-mono rpt-stat-label">{AUTO_COST_PER_LABELS[cp.key]}</span>
                  </p>
                ))}
                <p className="rpt-stat">
                  <Figure
                    value={money ? money.map((m) => moneyFull(m.spend, m.currency)).join(' · ') : '0'}
                    className="rpt-card-figure"
                  />
                  <span className="rpt-mono rpt-stat-label">Spend</span>
                </p>
              </div>
              {extras.length > 0 && (
                <p className="rpt-sub rpt-muted" data-auto-extras>
                  {/* ⚠️ Extras are NUMBERS, not efficiency metrics — no cost-per here. */}
                  Also from these campaigns:{' '}
                  {extras.map((k) => `${countText(panel, k)} ${AUTO_COUNT_LABELS[k].toLowerCase()}`).join(' · ')}
                </p>
              )}
            </div>
            {panel.charts.length > 0 && (
              <div className="rpt-card-grid rpt-card-grid--2" style={{ marginTop: 14 }}>
                {panel.charts.map((fam) => (
                  <AutoFamilyChart
                    key={fam.key}
                    family={fam}
                    section={spec.title}
                    labels={labels}
                    fallbackCurrency={fallbackCurrency}
                    today={today}
                  />
                ))}
              </div>
            )}
            {spec.campaignTable && <AutoCampaignTable panel={panel} />}
            <AutoPanelCreatives panel={panel} />
          </>
        )}
      </section>
    </Band>
  );
}

/**
 * The sections, in spec order, hide-when-empty applied, each FULL WIDTH.
 *
 * ⚠️ One chart per METRIC FAMILY, not one per panel: the chart card plots one count
 * (with its cost-per and spend) at a time, and the AIA panel has two goals (content
 * views and leads), so it carries two cards, side by side inside its own section.
 */
export function AutomotiveMetaPanels({
  auto,
  labels,
  today,
}: {
  auto: PortalMetaAutomotiveReport;
  labels: PortalReportLabels;
  today: string;
}) {
  const showContacts = contactsVisible(auto.headline);
  const visible = auto.panels.filter(autoPanelVisible);
  return (
    <>
      <Band>
        <SectionHeading title="Results by campaign type" intro={AUTO_RESULTS_INTRO} />
      </Band>
      {visible.map((p, i) => (
        <AutoPanelSection
          key={p.bucket}
          panel={p}
          spec={autoPanelSpecFor(p.bucket)}
          showContacts={showContacts}
          labels={labels}
          today={today}
          alternate={i % 2 === 1}
        />
      ))}
    </>
  );
}

/**
 * The whole automotive Meta page: headline, the all-campaigns charts, the sections
 * (each with its own charts and top creatives), then the audience and delivery
 * sections the standard Meta page carries (Round 5 §D13).
 *
 * ⚠️ The all-campaigns band carries leads and content views (and, through the
 * leads family, spend and cost per lead); the cost-per rule means content views
 * there have NO cost-per — a cost over mixed-goal spend is not an honest figure.
 *
 * ⚠️ `meta` is the page's own payload and is OPTIONAL only so the panels can be
 * rendered on their own in a test: the audience and delivery sections draw only
 * when it is given. The audience section is omitted on a group scope exactly as on
 * the standard page (a single location's absence is a fault and says so).
 */
export function AutomotiveMetaPage({
  auto,
  range,
  labels,
  today,
  meta,
}: {
  auto: PortalMetaAutomotiveReport;
  range: { from: string; to: string };
  labels: PortalReportLabels;
  today: string;
  meta?: PortalMetaReport;
}) {
  const fallbackCurrency = auto.panels.find((p) => p.money.length > 0)?.money[0]?.currency ?? 'AUD';
  return (
    <>
      <AutomotiveMetaHeadline auto={auto} range={range} />
      {auto.charts.length > 0 && (
        <Band>
          <SectionHeading
            title="Meta advertising results"
            intro="Choose between the past 12 months and the reporting period."
          />
          <div className="rpt-card-grid rpt-card-grid--2">
            {auto.charts.map((fam) => (
              <AutoFamilyChart
                key={fam.key}
                family={fam}
                section="All Meta campaigns"
                labels={labels}
                fallbackCurrency={fallbackCurrency}
                today={today}
              />
            ))}
          </div>
        </Band>
      )}
      <AutomotiveMetaPanels auto={auto} labels={labels} today={today} />
      {meta && !isGroupScope(meta.accounts) && (
        <Band>
          <AudiencePanel demographics={meta.demographics} today={today} />
        </Band>
      )}
      {meta && (
        <Band>
          <DeliveryDiagnostics
            campaigns={meta.campaigns}
            block={meta.block}
            labels={labels}
            periodLabel="this period"
          />
        </Band>
      )}
    </>
  );
}
